import { Component, ViewChild, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { REFERRAL_STATUS } from '../../../../constants/referral.constants';
import { ErrorMessages } from '../../../../config/error-messages';
import { SpinnerComponent } from '../../../shared/spinner/spinner.component';
import { ServiceRouter } from '../../../../services/core/http-service-router.service';
import { METHOD_TYPE } from '../../../../constants/service.constants';
import { Config } from '../../../../config/config';
import { Environment } from '../../../../config/environment';
import { PrintService } from 'src/app/services/core/print.service';
import { Subscription } from 'rxjs';
import { AppointmentsService } from 'src/app/services/scheduling/appointment/appointments.service';
import { PROCEDURE_CONSTANTS, APPOINTMENT_STATUS } from 'src/app/constants/scheduling.constants';
import { ENgxPrintComponent } from '../../../shared/ngx-print-block/e-ngx-print.component';

/**
 * Summary component
 */
@Component({
	selector: 'app-referral-summary',
	templateUrl: './summary.component.html',
	styleUrls: ['./summary.component.scss'],
})
export class SummaryComponent implements OnDestroy {
	/**  */
	@ViewChild('print2', { static: false }) printComponent: ENgxPrintComponent;
	printCSS = [this.config.print.css.filePath];
	/** Data carrier subscription  */
	subscriptions: Subscription[] = [];
	/** Summary table content */
	tableContent = '';
	/** Referral Summary data */
	data: any;
	/** Current Referral flag */
	isCurrentReferral: boolean = false;
	/** Referral status */
	referralStatus: any = REFERRAL_STATUS;
	/** No referral data message string */
	noDataMsg: string;
	/** No referral data flag */
	noData: boolean = false;
	/** Show request sent message flag */
	showRequestSentMsg: boolean = false;
	/** Member eligibility flag */
	hasEligibility: boolean = false;
	/** Member eligibility message */
	eligibilityMessage = '';
	/** Procedure constants */
	procedureConstants = PROCEDURE_CONSTANTS;
	/** Appointment status constants */
	apptStatus = APPOINTMENT_STATUS;

	/**
	 * Summary constructor
	 * @param activatedRoute Contains the information about route associated
	 * @param errorMsg Error messages
	 * @param spinner Spinner component
	 * @param serviceRouter service router
	 * @param config config file that holds the service end points
	 * @param environment environment file that supplies env values
	 * @param router angular router
	 * @param printService Print service
	 * @param appointmentService Appointment service
	 */
	constructor(
		private activatedRoute: ActivatedRoute,
		private errorMsg: ErrorMessages,
		private spinner: SpinnerComponent,
		private serviceRouter: ServiceRouter,
		private config: Config,
		private environment: Environment,
		public router: Router,
		private printService: PrintService,
		private appointmentService: AppointmentsService
	) {
		this.subscriptions.push(
			this.activatedRoute.params.subscribe(params => {
				try {
					this.isCurrentReferral = params.section === 'current';
				} catch (Exception) {}
				if (this.isCurrentReferral) {
					this.getReferrals();
					this.getCurrentReferralSummary();
				} else {
					this.getReferrals();
					this.getPastReferralSummary();
				}
			})
		);
		this.subscriptions.push(
			this.activatedRoute.queryParams.subscribe(queryParams => {
				this.showRequestSentMsg = queryParams.appointmentRequestSent;
			})
		);
	}

	/**
	 * Retrieve current referral summary list
	 */
	getCurrentReferralSummary() {
		this.noData = false;
		this.data = '';
		this.spinner.startLoader();
		this.subscriptions.push(
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.referralApi.getCurrentReferrals + '?category=summary',
					METHOD_TYPE.SECURED_GET,
					''
				)
				.subscribe(
					response => {
						this.spinner.stopLoader();
						this.data = response;
						if (!this.data || !this.data.category || this.data.category.length === 0) {
							this.noData = true;
							this.noDataMsg = this.errorMsg.noData.currentReferrals;
						}
					},
					err => {
						this.spinner.stopLoader();
						this.noData = true;
						this.noDataMsg = this.errorMsg.noData.currentReferrals;
					}
				)
		);
	}

	/**
	 * Retrieve past referral summary list
	 */
	getPastReferralSummary() {
		this.noData = false;
		this.data = '';
		this.spinner.startLoader();
		this.subscriptions.push(
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.referralApi.getPastReferrals + '?category=summary',
					METHOD_TYPE.SECURED_GET,
					''
				)
				.subscribe(
					response => {
						this.spinner.stopLoader();
						this.data = response;
						if (!this.data || !this.data.category || this.data.category.length === 0) {
							this.noData = true;
							this.noDataMsg = this.errorMsg.noData.currentReferrals;
						}
					},
					err => {
						this.spinner.stopLoader();
						this.noData = true;
						this.noDataMsg = this.errorMsg.noData.currentReferrals;
					}
				)
		);
	}
	redirect(status) {
		if (this.isCurrentReferral) {
			if (status === 'My Requests') {
				this.router.navigate(['/wellness/referral/current/requests']);
			} else if (status === 'Referral Ready') {
				this.router.navigate(['/wellness/referral/current/ready']);
			} else if (status === 'Pending Appointments') {
				this.router.navigate(['/wellness/referral/current/pending/appointment/request']);
			} else if (status === 'Confirmed Appointments') {
				this.router.navigate(['/wellness/referral/current/confirmed/appointments']);
			}
		} else {
			if (status === 'My Requests') {
				this.router.navigate(['/wellness/referral/history/requests']);
			} else if (status === 'Referral Ready') {
				this.router.navigate(['/wellness/referral/history/ready']);
			} else if (status === 'Pending Appointments') {
				this.router.navigate(['/wellness/referral/history/pending/appointment/request']);
			} else if (status === 'Confirmed Appointments') {
				this.router.navigate(['/wellness/referral/history/confirmed/appointments']);
			}
		}
	}

	/**
	 * Retrieve referral information for member eligibility message
	 */
	getReferrals() {
		this.spinner.startLoader();
		this.subscriptions.push(
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.referralApi.getCurrentReferrals,
					METHOD_TYPE.SECURED_GET,
					''
				)
				.subscribe(
					(data: any) => {
						this.spinner.stopLoader();
						if (data.patientEligiblity) {
							this.hasEligibility = true;
						}
					},
					err => {
						// Error
						this.spinner.stopLoader();
						console.error(`[${SummaryComponent.name}][${this.getReferrals.name}]`, err);
					}
				)
		);
	}

	/**
	 * Constructing table content for printing referral information
	 */
	getTableContent() {
		this.tableContent = '';
		this.data.category.forEach(category => {
			category.referral.forEach(referral => {
				this.tableContent =
					this.tableContent +
					'<tr>' +
					'<td>' +
					referral.specialtyName +
					'</td>' +
					'<td>' +
					referral.lastUpdatedDate +
					'</td>' +
					'<td>' +
					this.referralStatus[category.name] +
					'</td>' +
					'</tr> ';
			});
		});
		return this.tableContent;
	}

	/**
	 * Prints Referral information in a tabular format
	 */
	customPrint() {
		const tableHeader = '<th>Specialty</th><th>Last Update</th><th>Status</th>';
		const tableContent = this.getTableContent();
		this.printService.printDocument(tableHeader, tableContent, this.printComponent, 'REFERRAL LIST');
	}

	/**
	 * Destroys the component
	 */
	ngOnDestroy() {
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
		this.subscriptions = [];
	}
}
